import { QueryClient } from '@tanstack/react-query';

/**
 * Extracts an HTTP status code from error messages thrown by ApiClient.getJson()
 * which follow the pattern: "${status}: ${errorText}"
 */
function extractStatusCode(error: unknown): number | null {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const match = message.match(/^(\d{3}):/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Determines whether a failed query should be retried.
 *
 * - Client errors (4xx) are never retried — the request won't succeed without changes.
 * - Known auth/permission keywords are never retried.
 * - Server errors (5xx) and network failures are retried up to 2 times.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = extractStatusCode(error);

  // Never retry client errors
  if (status !== null && status >= 400 && status < 500) {
    return false;
  }

  // Never retry known non-transient keywords
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (
    message.includes('Unauthorized') ||
    message.includes('Forbidden') ||
    message.includes('Not Found') ||
    message.includes('MFA')
  ) {
    return false;
  }

  // Retry transient failures (5xx, network) up to 2 times
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: shouldRetry,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});
